import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import {
  fetchEarningsAnalytics,
  fetchRecentTransactions,
} from '../../store/slices/adminSlice';

const AdminEarningsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [chartPeriod, setChartPeriod] = useState('12months');
  const dispatch = useDispatch<AppDispatch>();

  const { earningsAnalytics, recentTransactions: liveTransactions } = useSelector(
    (state: RootState) => state.admin
  );

  useEffect(() => {
    dispatch(fetchEarningsAnalytics());
    dispatch(fetchRecentTransactions(8));
  }, [dispatch]);

  // Use live data
  const totalRevenue = earningsAnalytics?.totalRevenue || 0;
  const currentMonthRevenue = earningsAnalytics?.currentMonthRevenue || 0;
  const totalFeesCollected = earningsAnalytics?.totalFees || 0;
  const activeWallets = earningsAnalytics?.activeWallets || 0;
  const earningsChartData = (earningsAnalytics?.revenueByMonth || []).map((item) => ({
    month: item.name,
    revenue: item.revenue,
  }));

  // Revenue breakdown derived from live totals
  const revenueBreakdownData = [
    { name: 'Transaction Fees', value: totalFeesCollected, color: '#11C1EC' },
    { name: 'Platform Revenue', value: totalRevenue, color: '#00C48C' },
    { name: 'Current Month', value: currentMonthRevenue, color: '#FFB946' },
  ];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatCurrencyDetailed = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount);
  };

  const StatCard: React.FC<{
    label: string;
    value: string;
    subtext?: string;
    trend?: string;
  }> = ({ label, value, subtext, trend }) => (
    <Card sx={{ borderRadius: 2, p: 2.5, height: '100%' }}>
      <Box>
        <Typography variant="body2" sx={{ color: '#7F839C', mb: 1, fontSize: '0.875rem' }}>
          {label}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#11C1EC', mb: 0.5 }}>
          {value}
        </Typography>
        {subtext && (
          <Typography variant="caption" sx={{ color: '#7F839C' }}>
            {subtext}
          </Typography>
        )}
        {trend && (
          <Typography
            variant="caption"
            sx={{ color: trend.includes('+') ? '#00C48C' : '#FF4757', display: 'block', mt: 0.5 }}
          >
            {trend}
          </Typography>
        )}
      </Box>
    </Card>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Admin Earnings & Revenue
        </Typography>
        <Typography variant="body2" sx={{ color: '#7F839C' }}>
          Track platform revenue and transaction fees
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            label="Total Revenue"
            value={formatCurrency(totalRevenue)}
            subtext="All time"
            trend="+12% YoY"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            label="Monthly Revenue"
            value={formatCurrency(currentMonthRevenue)}
            subtext="March 2026"
            trend="+8% vs Feb"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            label="Total Fees Collected"
            value={formatCurrencyDetailed(totalFeesCollected)}
            subtext="Last 8 transactions"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <StatCard
            label="Active Wallets"
            value={activeWallets.toLocaleString()}
            subtext="Registered users"
            trend="+2.3% this month"
          />
        </Grid>
      </Grid>

      {/* Revenue Chart */}
      <Card sx={{ borderRadius: 2, p: 3, mb: 4 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Revenue Trend
          </Typography>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={chartPeriod}
              label="Period"
              onChange={(e) => setChartPeriod(e.target.value)}
            >
              <MenuItem value="3months">Last 3 Months</MenuItem>
              <MenuItem value="6months">Last 6 Months</MenuItem>
              <MenuItem value="12months">Last 12 Months</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <ResponsiveContainer width="100%" height={350}>
          <AreaChart
            data={earningsChartData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#11C1EC" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#11C1EC" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E8E8F0" />
            <XAxis dataKey="month" stroke="#7F839C" style={{ fontSize: '12px' }} />
            <YAxis stroke="#7F839C" style={{ fontSize: '12px' }} />
            <Tooltip
              contentStyle={{
                backgroundColor: '#ffffff',
                border: '1px solid #E8E8F0',
                borderRadius: '8px',
              }}
              formatter={(value) => formatCurrency(value as number)}
              labelFormatter={(label) => `${label}`}
            />
            <Area
              type="monotone"
              dataKey="revenue"
              stroke="#11C1EC"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorRevenue)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Revenue Breakdown */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 2, p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
              Revenue Breakdown
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={revenueBreakdownData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }: any) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {revenueBreakdownData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value as number)} />
              </PieChart>
            </ResponsiveContainer>
            <Box sx={{ mt: 2 }}>
              {revenueBreakdownData.map((item) => (
                <Box key={item.name} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: item.color,
                      mr: 1.5,
                    }}
                  />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {item.name}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatCurrency(item.value)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Card>
        </Grid>

        {/* Fee Rate Breakdown */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 2, p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
              Fee Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={[
                  { category: 'Domestic', fees: 285000, transactions: 1500 },
                  { category: 'International', fees: 165000, transactions: 620 },
                ]}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E8F0" />
                <XAxis dataKey="category" stroke="#7F839C" />
                <YAxis stroke="#7F839C" />
                <Tooltip formatter={(value) => formatCurrency(value as number)} />
                <Legend />
                <Bar dataKey="fees" fill="#11C1EC" name="Fees Collected" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Transactions Table */}
      <Card sx={{ borderRadius: 2 }}>
        <Box sx={{ p: 3, borderBottom: '1px solid #E8E8F0' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Recent Transactions
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F8F8FC' }}>
                <TableCell sx={{ fontWeight: 700 }}>Transaction ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                {!isMobile && <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>}
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Amount
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Fee
                </TableCell>
                {!isMobile && <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {liveTransactions.map((transaction) => (
                <TableRow key={transaction.id} sx={{ '&:hover': { backgroundColor: '#F8F8FC' } }}>
                  <TableCell sx={{ fontWeight: 600, color: '#11C1EC' }}>
                    {transaction.id.substring(0, 8)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{transaction.user}</Typography>
                  </TableCell>
                  {!isMobile && (
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(transaction.date).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: '#202647' }}
                    >
                      {formatCurrencyDetailed(transaction.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: '#00C48C' }}
                    >
                      +{formatCurrencyDetailed(transaction.fee)}
                    </Typography>
                  </TableCell>
                  {!isMobile && (
                    <TableCell>
                      <Chip
                        label={transaction.status}
                        color="success"
                        size="small"
                      />
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
};

export default AdminEarningsPage;
